import React from 'react'
import Avatar from 'components/Avatar'
import { FormGroupContainer, FormRow } from 'components/inputs'
import {
  CustomSelect,
  SingleItemSelectedDefault,
  useCustomSelectStateFromInfiniteScrollProvider,
} from 'components/inputs/CustomSelect'
import SearchResultItem from 'components/inputs/CustomSelect/SearchResultItem'
import Member from 'iam/models/Member'
import { observer } from 'mobx-react'
import AdvisorSelectViewModel from './AdvisorSelectViewModel'

interface AdvisorSelectProps {
  vm: AdvisorSelectViewModel
  disabled?: boolean
}

function advisorName(member: Member) {
  return member.displayName || member.email
}

/**
 * Picker for the advisor who will represent an invited client.
 * An empty choice assigns no one.
 */
export default observer(function AdvisorSelect({
  vm,
  disabled,
}: AdvisorSelectProps) {
  const selectProps = useCustomSelectStateFromInfiniteScrollProvider({
    scrollProvider: vm.scrollProvider,
    searchText: vm.searchText.value,
    onSearchTextChange: vm.triggerSearch,
  })

  return (
    <FormRow>
      <FormGroupContainer
        label="Assign to"
        help="The advisor who will represent this client. Existing clients are assigned instead of invited."
      >
        <CustomSelect
          {...selectProps}
          multi={false}
          value={vm.selected ?? undefined}
          placeholder="No one"
          disabled={disabled}
          itemToString={(member) => (member ? advisorName(member) : '')}
          areItemsEqual={(left, right) =>
            left?.user.publicId === right?.user.publicId
          }
          onChange={vm.select}
          data-test="assign-advisor-selector"
          renderItem={(itemProps) => (
            <SingleItemSelectedDefault
              {...itemProps}
              visual={
                <Avatar
                  size={24}
                  subject={{
                    avatar: itemProps.item.avatar,
                    displayName: advisorName(itemProps.item),
                  }}
                />
              }
            />
          )}
          renderSearchItem={({ item, ...itemProps }) => (
            <SearchResultItem
              {...itemProps}
              visual={
                <Avatar
                  size={24}
                  subject={{
                    avatar: item.avatar,
                    displayName: advisorName(item),
                  }}
                />
              }
            >
              {advisorName(item)}
            </SearchResultItem>
          )}
        />
      </FormGroupContainer>
    </FormRow>
  )
})
